import React from 'react';
import { 
  Box, 
  Flex, 
  Text, 
  Link as ChakraLink, 
  useColorModeValue, 
  Icon, 
  Link, 
  VStack, 
  HStack, 
  Grid,
  GridItem,
  Button,
  useColorMode,
  Divider,
  Image,
} from '@chakra-ui/react';
import { 
  FaFacebook, 
  FaInstagram, 
  FaYoutube, 
  FaTwitter, 
  FaEnvelope, 
  FaPhone, 
  FaMapMarkerAlt,
  FaArrowRight
} from 'react-icons/fa';
import { motion } from 'framer-motion';
import Logo from '../media/logo_transparent.png';

const Footer = () => {
  const textColor = useColorModeValue('gray.600', 'gray.300');
  const headingColor = useColorModeValue('gray.800', 'white');
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const { colorMode } = useColorMode();


  const footerLinks = [
    {
      title: "Quick Links",
      links: [
        { name: "Home", href: "/" },
        { name: "About Us", href: "/about" },
        { name: "Events", href: "/events" },
        { name: "Gallery", href: "/gallery" },
        { name: "Contact", href: "/contact" },
        { name: "Charity Info", href: "/https://find-and-update.company-information.service.gov.uk/company/07432977" },
      ]
    }
  ];

  const socialLinks = [
    { icon: FaFacebook, href: "https://www.facebook.com/ayouthuk", color: "#4267B2", label: "Facebook" },
    { icon: FaInstagram, href: "https://www.instagram.com/alphayouthuk", color: "#E1306C", label: "Instagram" },
    { icon: FaYoutube, href: "https://www.youtube.com/@Alphayouthuk", color: "#FF0000", label: "YouTube" },
    { icon: FaTwitter, href: "https://twitter.com/alphayouthuk", color: "#1DA1F2", label: "Twitter" },
  ];

  const contactInfo = [
    { icon: FaEnvelope, text: "alphayouthuk10@gmail.com" },
    { icon: FaPhone, text: "+44 7949 750971" },
    { icon: FaMapMarkerAlt, text: "248 London Rd, Romford RM7 9EL" },
  ];

  return (
    <Box 
      bg={bgColor}
      color={textColor}
      borderTopWidth="1px"
      borderTopColor={borderColor}
      pt={12}
      pb={8}
      px={{ base: 4, md: 8, lg: 12 }}
    >
      <Grid
        templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" }}
        gap={8}
        maxW="1400px"
        mx="auto"
        mb={8}
      >
        {/* Logo and Description */}
        <GridItem>
          <VStack align="start" spacing={4}>
            <Flex align="center">
              <Image
                src={Logo}
                alt="Alpha Youth UK Logo"
                boxSize="70px"
                objectFit="contain"
                mr={3}
              />
              <Text 
                fontSize="xl" 
                fontWeight="bold"
                bgGradient="linear(to-r, purple.600, orange.500)"
                bgClip="text"
              >
                Alpha Youth UK
              </Text>
            </Flex>
            <Text fontSize="sm">
              Empowering the next generation through faith, fellowship, and service. 
              A registered charity in England and Wales (1145081).
            </Text>
            <Button
              as={Link}
              href="https://find-and-update.company-information.service.gov.uk/company/07432977"
              colorScheme="purple"
              variant="outline"
              size="sm"
              rightIcon={<FaArrowRight />}
              borderRadius="full"
              _hover={{
                bgGradient: "linear(to-r, purple.600, orange.500)",
                color: "white",
                transform: "translateX(4px)"
              }}
              transition="all 0.3s ease"
            >
              Learn More
            </Button>
          </VStack>
        </GridItem>

        {/* Footer Links */}
        {footerLinks.map((section, index) => (
          <GridItem key={index}>
            <VStack align="start" spacing={4}>
              <Text 
                fontSize="lg" 
                fontWeight="bold" 
                color={headingColor}
                mb={2}
              >
                {section.title}
              </Text>
              {section.links.map((link, linkIndex) => (
                <motion.div
                  key={linkIndex}
                  whileHover={{ x: 4 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <ChakraLink
                    href={link.href}
                    fontSize="sm"
                    _hover={{ 
                      color: colorMode === 'light' ? 'purple.600' : 'purple.300',
                      textDecoration: 'none'
                    }}
                  >
                    {link.name}
                  </ChakraLink>
                </motion.div>
              ))}
            </VStack>
          </GridItem>
        ))}

        {/* Contact Info */}
        <GridItem>
          <VStack align="start" spacing={4}>
            <Text 
              fontSize="lg" 
              fontWeight="bold" 
              color={headingColor}
              mb={2}
            >
              Contact Us
            </Text>
            {contactInfo.map((item, index) => (
              <HStack key={index} spacing={3} align="start">
                <Icon as={item.icon} mt={0.5} color="purple.500" />
                <Text fontSize="sm">{item.text}</Text>
              </HStack>
            ))}
            
            <Text 
              fontSize="lg" 
              fontWeight="bold" 
              color={headingColor}
              mt={4}
              mb={2}
            >
              Follow Us
            </Text>
            <HStack spacing={4}>
              {socialLinks.map((social, index) => (
                <motion.div
                  key={index}
                  whileHover={{ y: -2, scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <ChakraLink
                    href={social.href}
                    isExternal
                    aria-label={social.label}
                  >
                    <Icon 
                      as={social.icon} 
                      boxSize={5} 
                      color={social.color}
                      _hover={{ opacity: 0.8 }}
                    />
                  </ChakraLink>
                </motion.div>
              ))}
            </HStack>
          </VStack>
        </GridItem>
      </Grid>

      <Divider borderColor={borderColor} my={8} />

      <Flex
        direction={{ base: "column-reverse", md: "row" }}
        justify="space-between"
        align="center"
        maxW="1400px"
        mx="auto"
        pt={4}
      >
        <Text fontSize="xs" textAlign={{ base: "center", md: "left" }}>
          © {new Date().getFullYear()} Alpha Youth UK. All rights reserved. 
          Charity registered in England & Wales (1145081).
        </Text>
        
        <HStack spacing={4} mb={{ base: 4, md: 0 }}>
          <Text fontSize="xs">
            Website by <ChakraLink 
              href="https://www.instagram.com/bbusari01/" 
              isExternal
              fontWeight="semibold"
              _hover={{ color: "orange.500" }}
            >
              Boladale
            </ChakraLink>
          </Text>
        </HStack>
      </Flex>
    </Box>
  );
};

export default Footer;